import React from "react"
import styles from "./WebSearch.module.scss"

interface MetaUrl {
    favicon?: string
    hostname?: string
    path?: string
}

interface Thumbnail {
    src?: string
    original?: string
}

interface WebResult {
    title?: string
    url?: string
    description?: string
    meta_url?: MetaUrl
    thumbnail?: Thumbnail
    age?: string
    [key: string]: any
}

interface VideoResult {
    title?: string
    url?: string
    description?: string
    thumbnail?: Thumbnail
    meta_url?: MetaUrl
    video?: { duration?: string; views?: number; creator?: string }
    age?: string
    [key: string]: any
}

interface SearchData {
    data?: {
        query?: { original?: string }
        web?: { results?: WebResult[] }
        videos?: { results?: VideoResult[] }
        images?: { results?: { url?: string; thumbnail?: Thumbnail }[] }
        mixed?: { main?: { type: string; index?: number; all?: boolean }[] }
        [key: string]: any
    }
    [key: string]: any
}

const WebSearch: React.FC<{
    webSearchResults: SearchData
    setWebSearchResults: React.Dispatch<React.SetStateAction<any>>
}> = ({ webSearchResults }) => {
    const data = webSearchResults?.data || {}
    const query = data?.query?.original || "Unknown Query"

    // Explicit type guards for nested properties
    const webResults = data.web && Array.isArray(data.web.results) ? data.web.results : []
    const videoResults =
        data.videos && Array.isArray(data.videos.results) ? data.videos.results : []
    const imageResults =
        data.images && Array.isArray(data.images.results) ? data.images.results : []
    const mixedResults = data.mixed && Array.isArray(data.mixed.main) ? data.mixed.main : []

    // Fallback to web results if mixed.main is empty or invalid
    const orderedResults =
        mixedResults.length > 0
            ? mixedResults
                  .map((item) => {
                      if (
                          item?.type === "web" &&
                          typeof item.index === "number" &&
                          webResults[item.index]
                      ) {
                          return webResults[item.index]
                      } else if (item?.type === "videos" && item.all) {
                          return { type: "videos", results: videoResults }
                      }
                      return null
                  })
                  .filter((item) => item !== null)
            : webResults

    return (
        <div className={styles.webSearch}>
            <div className={styles.container}>
                <div className={styles.mainResults}>
                    <div className={styles.staticSearchBar}>
                        <i className="material-symbols-rounded">search</i>
                        {query}
                    </div>
                    {orderedResults.length === 0 && (
                        <p className={styles.noResults}>No results found.</p>
                    )}
                    {orderedResults.map((result, idx) => {
                        if (result && "type" in result && result.type === "videos") {
                            return (
                                <div key={`videos-${idx}`} className={styles.videoSection}>
                                    <h3>Videos</h3>
                                    <div className={styles.videoGrid}>
                                        {result.results.map((video: VideoResult, vIdx: number) => (
                                            <a
                                                key={`video-${vIdx}`}
                                                href={video?.url || "#"}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.videoItem}>
                                                {video?.thumbnail?.src && (
                                                    <img
                                                        src={video.thumbnail.src}
                                                        alt={video?.title || "Video thumbnail"}
                                                        className={styles.videoThumbnail}
                                                        onError={(e) =>
                                                            (e.currentTarget.style.display = "none")
                                                        }
                                                    />
                                                )}
                                                <div className={styles.videoInfo}>
                                                    <div className={styles.videoTitle}>
                                                        {video?.meta_url?.favicon && (
                                                            <img
                                                                src={video.meta_url.favicon}
                                                                alt={`${
                                                                    video?.meta_url?.hostname ||
                                                                    "site"
                                                                } favicon`}
                                                                className={styles.favicon}
                                                                onError={(e) =>
                                                                    (e.currentTarget.style.display =
                                                                        "none")
                                                                }
                                                            />
                                                        )}
                                                        <span>
                                                            {video?.title || "Untitled Video"}
                                                        </span>
                                                    </div>
                                                    {video?.description && (
                                                        <p className={styles.videoDescription}>
                                                            {video.description}
                                                        </p>
                                                    )}
                                                    {(video?.video?.creator ||
                                                        video?.video?.duration ||
                                                        video?.video?.views ||
                                                        video?.age) && (
                                                        <p className={styles.videoMeta}>
                                                            {video?.video?.creator &&
                                                                `${video.video.creator}`}
                                                            {video?.video?.duration &&
                                                                ` • ${video.video.duration}`}
                                                            {video?.video?.views &&
                                                                ` • ${video.video.views} views`}
                                                            {video?.age && ` • ${video.age}`}
                                                        </p>
                                                    )}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )
                        } else if (result) {
                            const webResult = result as WebResult
                            return (
                                <a
                                    key={`web-${idx}`}
                                    href={webResult?.url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.webResult}>
                                    <div className={styles.webResultHeader}>
                                        {webResult?.meta_url?.favicon && (
                                            <img
                                                src={webResult.meta_url.favicon}
                                                alt={`${
                                                    webResult?.meta_url?.hostname || "site"
                                                } favicon`}
                                                className={styles.favicon}
                                                onError={(e) =>
                                                    (e.currentTarget.style.display = "none")
                                                }
                                            />
                                        )}
                                        <div>
                                            {webResult?.meta_url?.hostname && (
                                                <p className={styles.hostname}>
                                                    {webResult.meta_url.hostname}
                                                </p>
                                            )}
                                            {webResult?.meta_url?.path && (
                                                <p className={styles.path}>
                                                    {webResult.meta_url.path}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.webContent}>
                                        <div className={styles.webText}>
                                            <h3 className={styles.webTitle}>
                                                {webResult?.title || "Untitled Result"}
                                            </h3>
                                            {webResult?.description && (
                                                <p
                                                    className={styles.webDescription}
                                                    dangerouslySetInnerHTML={{
                                                        __html: webResult.description,
                                                    }}
                                                />
                                            )}
                                            {webResult?.age && (
                                                <p className={styles.webMeta}>{webResult.age}</p>
                                            )}
                                        </div>
                                        {webResult?.thumbnail?.src && (
                                            <img
                                                src={webResult.thumbnail.src}
                                                alt={webResult?.title || "Thumbnail"}
                                                className={styles.webThumbnail}
                                                onError={(e) =>
                                                    (e.currentTarget.style.display = "none")
                                                }
                                            />
                                        )}
                                    </div>
                                </a>
                            )
                        }
                        return null
                    })}
                </div>
                <div className={styles.sidebar}>
                    {(videoResults.length > 0 || imageResults.length > 0) && (
                        <>
                            {videoResults.length > 0 && (
                                <div className={styles.sidebarSection}>
                                    <h3>Related Videos</h3>
                                    {videoResults.slice(0, 3).map((video, idx) => (
                                        <a
                                            key={`sidebar-video-${idx}`}
                                            href={video?.url || "#"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.sidebarVideo}>
                                            {video?.thumbnail?.src && (
                                                <img
                                                    src={video.thumbnail.src}
                                                    alt={video?.title || "Video thumbnail"}
                                                    className={styles.sidebarThumbnail}
                                                    onError={(e) =>
                                                        (e.currentTarget.style.display = "none")
                                                    }
                                                />
                                            )}
                                            <div>
                                                <p className={styles.sidebarTitle}>
                                                    {video?.title || "Untitled Video"}
                                                </p>
                                                <p className={styles.sidebarMeta}>
                                                    {video?.video?.creator ||
                                                        video?.meta_url?.hostname ||
                                                        "Unknown Source"}
                                                    {video?.age && ` • ${video.age}`}
                                                </p>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                            {imageResults.length > 0 && (
                                <div className={styles.sidebarSection}>
                                    <h3>Related Images</h3>
                                    <div className={styles.imageGrid}>
                                        {imageResults.slice(0, 4).map((image, idx) => (
                                            <a
                                                key={`sidebar-image-${idx}`}
                                                href={image?.url || "#"}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {image?.thumbnail?.src && (
                                                    <img
                                                        src={image.thumbnail.src}
                                                        alt={`${idx + 1}`}
                                                        className={styles.sidebarImage}
                                                        onError={(e) =>
                                                            (e.currentTarget.style.display = "none")
                                                        }
                                                    />
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default WebSearch
